import { Inject, Injectable } from '@angular/core';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
  AuthServiceProvider,
  FactoryDto,
  MembershipDto,
  MembershipServiceInterface,
  ModalHelperService,
  PermissionDto,
  PictureStorageService,
  StorageServiceInterface,
  UserApiService,
} from 'common';
import { catchError, delay, distinctUntilChanged, filter, map, mergeMap, take, takeWhile, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ResourceService } from './resource.service';

@Injectable({
  providedIn: 'root'
})
export class MembershipService implements MembershipServiceInterface {

  private _permissionSub = new ReplaySubject<PermissionDto[]>(1);

  static lastSelectedFactoryIdKey = 'lastSelectedFactoryId';
  private static selectedFactoryIdObs = new ReplaySubject<string>(1);

  private _selectedFactoryId: string | undefined = undefined;
  private waitingObservables: Subject<MembershipDto[]>[] = [];
  private _memberships: MembershipDto[] | undefined;
  private lastEmittedValue: string | undefined;
  private invitationInWaiting?: { invitationId: string; token: string };

  constructor(@Inject('StorageService') private storageService: StorageServiceInterface,
              private resourceService: ResourceService,
              private modalHelperService: ModalHelperService,
              private translateService: TranslateService,
              private authProvider: AuthServiceProvider,
              private pictureStorageService: PictureStorageService,
              private router: Router,
              private userApiService: UserApiService) {
    this.storageService.getItems('permissions')
      .subscribe(permissions => {
        this._permissionSub.next(permissions as PermissionDto[]);
      });
  }

  loadMembership(forceEventEmit: boolean = false): Observable<any> {
    // load memberships
    return this.authProvider.getService()
      .pipe(
        mergeMap(service => service.isLogged()),
        filter(logged => logged),
        mergeMap(() => this.userApiService.getMyMemberships()),
        tap((memberships: MembershipDto[]) => {
          this._memberships = memberships;
          // clean last selected membership if not present in the list
          if (!this._memberships.filter(value => value.subscribable.id === this._selectedFactoryId).length) {
            this.removeSelectedFactory();
          }
          this.waitingObservables.forEach((s: Subject<MembershipDto[]>) => {
            s.next(this._memberships);
            s.complete();
          });
          if (this._selectedFactoryId) {
            const filtered = memberships.filter(m => m.subscribable.id === this._selectedFactoryId);
            if (filtered.length) {
              // apply the lang of the factory
              this.translateService.currentLang = (filtered[0].subscribable as FactoryDto).lang;
              this.translateService.setDefaultLang(this.translateService.currentLang);
              this.emitSelectedFactory(this._selectedFactoryId, forceEventEmit);
              // store permissions
              this.setPermissions(filtered[0].permissions);
            } else {
              this.selectFactory(undefined).subscribe();
            }
          }
          if (!this._selectedFactoryId && this._memberships.length === 1) {
            this.selectFactory(this._memberships[0].subscribable.id).subscribe();
          }
        })
      );
  }

  getSelectedMembershipListener(): Observable<MembershipDto | undefined> {
    return MembershipService.selectedFactoryIdObs
      .pipe(
        distinctUntilChanged(),
        filter(() => this._memberships !== undefined),
        map((factoryId) => factoryId ? this._memberships.filter(f => f.subscribable.id === factoryId)[0] : undefined)
      );
  }

  getSelectedMembershipObs(): Observable<MembershipDto | undefined> {
    return this.getSelectedMembershipListener()
      .pipe(take(1));
  }

  getSelectedFactoryIdObs(): Observable<string | undefined> {
    return MembershipService.selectedFactoryIdObs;
  }

  storeInvitationData(token: string, invitationId: string): void {
    this.invitationInWaiting = {
      token,
      invitationId
    };
  }

  hasInvitationInWaiting(): boolean {
    return !!this.invitationInWaiting;
  }

  acceptInvitationInWaiting(): Observable<any> | undefined {
    if (this.invitationInWaiting) {
      return of(void 0)
        .pipe(
          tap(() => this.modalHelperService.displayLoadingModal(this.translateService.instant('action.membershipInvitation.waiting'))),
          mergeMap(() => this.userApiService.acceptInvitation({
            token: this.invitationInWaiting.token,
            invitationId: this.invitationInWaiting.invitationId,
          })),
          mergeMap((membership: MembershipDto) => {
            // wait until the pictureStorageService.getImage doesn't return an error
            return this.pictureStorageService.getImage(membership.subscribable as FactoryDto, 'test.png')
              .pipe(
                map(() => true),
                catchError(() => of(false)),
                takeWhile((result: boolean) => result, true),
                delay(1000),
                map(() => membership),
              )
          }),
          tap((membership: MembershipDto) => {
            this.addAndSelect(membership);
            this.modalHelperService.closeLoadingModal();
            this.modalHelperService.displayToaster(this.translateService.instant('action.membershipInvitation.success'), 5000);
          }),
          catchError((err) => {
            this.modalHelperService.closeLoadingModal();
            if (err.message) {
              this.modalHelperService.displayToaster(err.message, 5000);
            } else {
              this.modalHelperService.displayToaster('action.membershipInvitation.failed', 5000);
            }
            throw err;
          })
        );
    }
  }

  selectFactory(factoryId: string, fragment?: string, forceNavigation: boolean = true): Observable<any> {
    if (this._selectedFactoryId === factoryId) {
      return of(void 0);
    }
    const oldFactoryId = this._selectedFactoryId;
    // force refresh token to add the user IAM role
    return this.authProvider.getService()
      .pipe(
        tap(service => service.forceRefreshToken()),
        mergeMap(() => this.getMemberships()),
        filter((memberships) => memberships.filter(m => m.subscribable.id === factoryId).length > 0),
        mergeMap((memberships: MembershipDto[]) => {
          // store factory
          const filteredMemberships = memberships.filter(m => m.subscribable.id === factoryId);
          if (factoryId && filteredMemberships.length) {
            this.storageService.storeItem(MembershipService.lastSelectedFactoryIdKey, factoryId).subscribe();
            // apply the lang of the factory
            this.translateService.currentLang = (filteredMemberships[0].subscribable as FactoryDto).lang;
            this.translateService.setDefaultLang(this.translateService.currentLang);
            // set permission
            return this.setPermissions(filteredMemberships[0].permissions);
          } else {
            this.removeSelectedFactory();
            this.storageService.removeItem(MembershipService.lastSelectedFactoryIdKey);
            this.clearPermission();
            return of(void 0);
          }
        }),
        tap(() => {
          // emit event
          this._selectedFactoryId = factoryId;
          this.emitSelectedFactory(this._selectedFactoryId);
          this.resourceService.forceReload();
          // navigate if required
          if (forceNavigation || window.location.pathname.indexOf('/app/home') >= 0) {
            if (factoryId) {
              if (window.location.pathname.indexOf(this._selectedFactoryId) < 0) {
                this.router.navigate(['app', this._selectedFactoryId, 'navigation', 'factory-structure'])
                  .then(() => {
                    if (!!oldFactoryId) {
                      window.location.reload();
                    }
                  });
              }
            } else {
              this.router.navigate(['app', 'home']);
            }
          }
        })
      );
  }


  navigateToSelectedMembership() {
    // extract selected factory
    // priority to the url param
    const match = window.location.pathname.match(/\/app\/([a-z0-9-]+)\//);
    let lastSelectedFactoryId;
    if (match) {
      lastSelectedFactoryId = match[1];
    } else {
      lastSelectedFactoryId = this.storageService.getItem(MembershipService.lastSelectedFactoryIdKey);
    }
    if (lastSelectedFactoryId) {
      this.selectFactory(lastSelectedFactoryId, null, false).subscribe();
    }
    this.loadMembership(false).subscribe();
  }

  getMemberships(forceReload?: boolean): Observable<MembershipDto[]> {
    if (forceReload) {
      this.loadMembership(forceReload).subscribe();
    } else if (this._memberships) {
      return of(this._memberships);
    }
    const sbj = new Subject<MembershipDto[]>();
    this.waitingObservables.push(sbj);
    return sbj;
  }

  removeSelectedFactory() {
    this._selectedFactoryId = null;
    this.storageService.removeItem(MembershipService.lastSelectedFactoryIdKey);
    this.resourceService.forceReload();
  }

  clearFactoryIfSelected(factoryId: string): Observable<any> {
    if (this._selectedFactoryId === factoryId) {
      this._selectedFactoryId = undefined;
      this.storageService.removeItem(MembershipService.lastSelectedFactoryIdKey);
      MembershipService.selectedFactoryIdObs.next(undefined);
      this.clearPermission();
    }
    return of(void 0);
  }

  refreshSelectedFactory(): void {
  }

  private emitSelectedFactory(value: string, force: boolean = false) {
    if ((this.lastEmittedValue !== value || force) && this._memberships) {
      MembershipService.selectedFactoryIdObs.next(value);
      this.lastEmittedValue = value;
    }
  }

  setPermissions(permissions: PermissionDto[]): Observable<void> {
    this.storageService.storeItems('permissions', permissions);
    this._permissionSub.next(permissions);
    return of(void 0);
  }

  clearPermission(): any {
    this._permissionSub.next([]);
  }

  private listPermissions(): Observable<PermissionDto[]> {
    return this.getSelectedMembershipListener().pipe(
      filter(membership => !!membership),
      map((membership) => membership.permissions)
    );
  }

  hasPermission(permission: PermissionDto): Observable<boolean> {
    return this.getPermissions()
      .pipe(
        map(permissions => permissions?.indexOf(permission) >= 0)
      );
  }

  hasAtLeastOnePermissions(expectedPermissions: PermissionDto[]): Observable<boolean> {
    return this.listPermissions()
      .pipe(
        map(permissions => expectedPermissions.some(expectedPermission => permissions.indexOf(expectedPermission) >= 0))
      );
  }

  private getPermissions(): Observable<PermissionDto[]> {
    return this._permissionSub.asObservable()
      .pipe(take(1));
  }

  private addAndSelect(membership: MembershipDto) {
    this.loadMembership(false)
      .pipe(
        mergeMap(() => this.selectFactory(membership.subscribable.id))
      )
      .subscribe();
  }
}
